import { api } from './api'; // Path to your base API client
import { DigiMagazine, DigiMagazineFile, DigiMagazinePayload } from '../types'; // Path to your type definitions

const API_PATHS = Object.freeze({
  digiMagazines: {
    root: '/digiMagazines',
    id: '/digiMagazines/{id}',
  },
});

export const getDigiMagazines = async (): Promise<DigiMagazine[]> => {
  const response = await api.get<DigiMagazine[]>(API_PATHS.digiMagazines.root, {});
  return response.data;
};

export const getSingleDigiMagazine = async (id: string): Promise<DigiMagazine> => {
  const response = await api.get<DigiMagazine>(API_PATHS.digiMagazines.id.replace('{id}', id), {});
  return response.data;
};

export const addSingleDigiMagazine = async (
  digiMagazinePayload: DigiMagazinePayload,
  filesToUpload: File[] // Issue-specific files (PDFs, covers)
): Promise<DigiMagazine> => {
  const formData = new FormData();
  for (const key in digiMagazinePayload) {
    if (Object.prototype.hasOwnProperty.call(digiMagazinePayload, key)) {
      // Skip fields that must not be sent as scalars
      const specialKeys = ['digiMagazineFiles', 'coverImageFile', 'image', 'files', 'id'];
      if (specialKeys.includes(key)) continue;
      const value = digiMagazinePayload[key as keyof DigiMagazinePayload];
      // Omit communityId entirely if empty string or null/undefined
      if (key === 'communityId' && (value === '' || value === undefined || value === null)) continue;
      if (value !== undefined && value !== null) formData.append(key, String(value));
    }
  }
  // Note: ID is only appended in update
  if (digiMagazinePayload.coverImageFile instanceof File) {
    formData.append('image', digiMagazinePayload.coverImageFile, digiMagazinePayload.coverImageFile.name);
  } else if (typeof digiMagazinePayload.image === 'string' && digiMagazinePayload.image) {
    formData.append('image', digiMagazinePayload.image);
  }

  if (digiMagazinePayload.files && Array.isArray(digiMagazinePayload.files)) {
    digiMagazinePayload.files.forEach((issueMetaData: DigiMagazineFile) => {
      if (typeof issueMetaData === 'object' && issueMetaData !== null) {
        const metaDataCopy: Record<string, unknown> = { ...issueMetaData } as Record<string, unknown>;
        if (Object.prototype.hasOwnProperty.call(metaDataCopy, 'coverImageFile')) {
          delete (metaDataCopy as { coverImageFile?: File }).coverImageFile;
        }
        // Backend expects repeated key without [] suffix
        formData.append('digiMagazineFiles', JSON.stringify(metaDataCopy));
      }
    });
  }
  if (filesToUpload && filesToUpload.length > 0) {
    filesToUpload.forEach((file) => {
      if (file instanceof File) formData.append('files', file, file.name);
    });
  }

  const response = await api.post<DigiMagazine>(API_PATHS.digiMagazines.root, formData, { timeout: 360000 });
  return response.data;
};

export const updateSingleDigiMagazine = async (
  digiMagazineId: number | string,
  digiMagazinePayload: DigiMagazinePayload,
  filesToUpload: File[]
): Promise<DigiMagazine> => {
  const formData = new FormData();
  for (const key in digiMagazinePayload) {
    if (Object.prototype.hasOwnProperty.call(digiMagazinePayload, key)) {
      // Skip fields that must not be sent as scalars
      const specialKeys = ['digiMagazineFiles', 'coverImageFile', 'image', 'files', 'id'];
      if (specialKeys.includes(key)) continue;
      const value = digiMagazinePayload[key as keyof DigiMagazinePayload];
      // Omit communityId entirely if empty string or null/undefined
      if (key === 'communityId' && (value === '' || value === undefined || value === null)) continue;
      if (value !== undefined && value !== null) formData.append(key, String(value));
    }
  }

  if (digiMagazinePayload.coverImageFile instanceof File) {
    formData.append('image', digiMagazinePayload.coverImageFile, digiMagazinePayload.coverImageFile.name);
  } else if (typeof digiMagazinePayload.image === 'string' && digiMagazinePayload.image) {
    formData.append('image', digiMagazinePayload.image);
  }

  // Only send changed issues to backend (new/edited/deleted)
  const changedFiles = Array.isArray(digiMagazinePayload.files)
    ? digiMagazinePayload.files.filter((f) => (f.state ?? '') !== '')
    : [];

  if (changedFiles.length > 0) {
    const deletedIds: number[] = [];
    changedFiles.forEach((issueMetaData: DigiMagazineFile) => {
      if (typeof issueMetaData === 'object' && issueMetaData !== null) {
        const state = (issueMetaData.state ?? '').toString();
        if (state === 'deleted') {
          if (issueMetaData.id != null) deletedIds.push(Number(issueMetaData.id));
          const minimalDelete = { id: issueMetaData.id, state: 'deleted', digiMagazineId };
          formData.append('digiMagazineFiles', JSON.stringify(minimalDelete));
          return;
        }

        // For new/edited, send a focused subset of fields + state
        const metaForUpdate: Record<string, unknown> = {
          id: issueMetaData.id,
          name: issueMetaData.name,
          description: issueMetaData.description ?? '',
          published: !!issueMetaData.published,
          order: issueMetaData.order,
          magazineNumber: issueMetaData.magazineNumber ?? '',
          releaseDate: issueMetaData.releaseDate,
          releaseYear: issueMetaData.releaseYear ?? null,
          src: issueMetaData.src ?? null,
          image: issueMetaData.image ?? null,
          state: state,
          digiMagazineId,
        };

        formData.append('digiMagazineFiles', JSON.stringify(metaForUpdate));
      }

      const maybeCover = (issueMetaData as unknown as { coverImageFile?: File }).coverImageFile;
      if (maybeCover instanceof File) {
        formData.append('files', maybeCover, maybeCover.name);
      }
    });

    if (deletedIds.length > 0) {
      formData.append('deletedFileIds', JSON.stringify(deletedIds));
    }
  }
  if (filesToUpload && filesToUpload.length > 0) {
    filesToUpload.forEach((file) => {
      if (file instanceof File) formData.append('files', file, file.name);
    });
  }

  const response = await api.put<DigiMagazine>(
    API_PATHS.digiMagazines.id.replace('{id}', `${digiMagazineId}`),
    formData,
    {
      timeout: 360000,
    }
  );
  return response.data;
};

export const deleteSingleDigiMagazine = async (id: string): Promise<void> => {
  return api.delete(API_PATHS.digiMagazines.id.replace('{id}', id), {});
};
